import { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, Route, Routes, useNavigate } from 'react-router-dom';
import { api, post } from './api.js';
import { useI18n } from './i18n.js';
import { LoginPage, SetupPage } from './pages/Auth.js';
import { WorkspacesPage } from './pages/Workspaces.js';
import { WorkspacePage } from './pages/Workspace.js';
import { WorkItemPage } from './pages/WorkItem.js';
import { OverlapPage } from './pages/Overlap.js';

interface AuthState {
  configured: boolean | null;
  actor: null | { id: string; kind: string; display_name?: string; email?: string };
}

export function App() {
  const [auth, setAuth] = useState<AuthState>({ configured: null, actor: null });
  const [error, setError] = useState('');

  const refreshAuth = async () => {
    setError('');
    const setup = await api<{ configured: boolean }>('/auth/setup/status');
    if (!setup.configured) {
      setAuth({ configured: false, actor: null });
      return;
    }
    try {
      const current = await api<{ actor: AuthState['actor'] }>('/auth/me');
      setAuth({ configured: true, actor: current.actor });
    } catch {
      setAuth({ configured: true, actor: null });
    }
  };

  useEffect(() => {
    void refreshAuth().catch((caught: Error) => setError(caught.message));
  }, []);

  if (error) return <FatalError message={error} onRetry={() => void refreshAuth()} />;
  if (auth.configured === null) return <LoadingScreen />;
  if (!auth.configured) return <SetupPage onComplete={refreshAuth} />;
  if (!auth.actor) return <LoginPage onComplete={refreshAuth} />;

  return (
    <Routes>
      <Route element={<Shell actor={auth.actor} onLogout={() => setAuth((value) => ({ ...value, actor: null }))} />}>
        <Route index element={<Navigate to="/workspaces" replace />} />
        <Route path="/workspaces" element={<WorkspacesPage />} />
        <Route path="/workspaces/:id" element={<WorkspacePage />} />
        <Route path="/work-items/:id" element={<WorkItemPage />} />
        <Route path="/overlaps/:id" element={<OverlapPage />} />
        <Route path="*" element={<Navigate to="/workspaces" replace />} />
      </Route>
    </Routes>
  );
}

function Shell({
  actor,
  onLogout
}: {
  actor: NonNullable<AuthState['actor']>;
  onLogout: () => void;
}) {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const logout = async () => {
    await post('/auth/logout', {});
    onLogout();
    navigate('/');
  };
  return (
    <div className="app-shell">
      <header className="topbar">
        <Link to="/workspaces" className="brand" aria-label={t('product')}>
          <span className="brand-mark">U</span>
          <span>
            <strong>{t('product')}</strong>
            <small>{t('tagline')}</small>
          </span>
        </Link>
        <div className="topbar-actions">
          <label className="locale-picker">
            <span className="sr-only">Language</span>
            <select value={locale} onChange={(event) => setLocale(event.target.value as 'ru' | 'en')}>
              <option value="ru">RU</option>
              <option value="en">EN</option>
            </select>
          </label>
          <span className="actor-name">{actor.display_name ?? actor.email ?? actor.id}</span>
          <button className="button ghost" onClick={() => void logout()}>{t('logout')}</button>
        </div>
      </header>
      <main className="page"><Outlet /></main>
    </div>
  );
}

function LoadingScreen() {
  return <div className="center-screen"><div className="spinner" aria-label="Loading" /></div>;
}

function FatalError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <div className="center-screen"><div className="panel auth-panel"><h1>UAW</h1><p className="error-box">{message}</p><button className="button primary" onClick={onRetry}>Retry</button></div></div>;
}
